import { useEffect, useMemo } from 'react';
import { ApolloError } from 'apollo-client';
import { useObserver } from 'mobx-react-lite';

import { useGetCollectionQuery } from 'types/graphql';
import useProductQueryParams from 'src/hooks/use-product-query-params';

import { parseProduct, Product } from 'utils/helpers/product';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';

type UseCollectionProps = {
  collectionId: string;
  dispensaryId: string;
  menuType?: string;
  limit?: number;
  offset?: number;
  sortBy?: string;
  skipCollectionQuery?: boolean;
};

type Schedule = {
  id?: string;
  startDate?: string;
  endDate?: string;
  timeRanges?: Array<{
    id?: string;
    dayOfWeek?: string;
    startTime?: string;
    endTime?: string;
  }>;
};

type CollectionWithProductsType = {
  id: string;
  title: string;
  description?: string;
  isExclusive?: boolean;
  cardBadgeColor?: string;
  image?: string;
  customCategory?: boolean;
  enabled?: boolean;
  isAccessibleToCurrentUser?: boolean;
  isOutlet?: boolean;
  outletTimerEnabled?: boolean;
  outletTimerMessage?: string;
  schedule?: Schedule;
  products: Product[];
  productCount: number;
};

export type CollectionDisplayMode = 'default' | 'exclusive' | 'hidden';

type UseCollectionReturn = {
  collection?: CollectionWithProductsType;
  loading: boolean;
  error?: ApolloError;
  collectionDisplayMode: CollectionDisplayMode;
};

/**
 * Hook to fetch collection data with products.
 *
 * Optimized for FCP/CLS: Initially uses collectionsNavigation from the dispensary object
 * (available immediately from SSR/cache) to render basic info without layout shift.
 * Then fetches full collection details via useGetCollectionQuery which includes
 * additional fields like description, image, isExclusive, and isAccessibleToCurrentUser.
 * Once the full query returns, it replaces the initial dispensary data.
 */
export function useCollections({
  collectionId,
  dispensaryId,
  limit = 30,
  sortBy: sortByOverride,
  skipCollectionQuery = false,
}: UseCollectionProps): UseCollectionReturn {
  const { queryParams: qp } = useProductQueryParams();
  const hasBrandsFilter = !!qp.brands.length;
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const { dispensary } = useDispensary();

  // Get collection from dispensary object (available immediately from SSR/cache)
  // Match by either id or slug since collectionId from URL could be either
  const dispensarySuppliedCollection = useMemo(() => {
    const collections = dispensary?.collectionsNavigation;
    if (!collections || !collectionId) {
      return null;
    }
    return collections.find((c) => c.id === collectionId || c.slug === collectionId) ?? null;
  }, [dispensary?.collectionsNavigation, collectionId]);

  // Fetch full collection details via separate query for complete data
  const { data: collectionData, loading: collectionLoading, error: collectionError, refetch } = useGetCollectionQuery({
    skip: !dispensaryId || skipCollectionQuery,
    variables: {
      collectionId,
      dispensaryId,
    },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  // Refetch collection when login state changes to get updated isAccessibleToCurrentUser
  useEffect(() => {
    if (!skipCollectionQuery && dispensaryId) {
      void refetch();
    }
    // Intentionally only depends on isLoggedIn to trigger refetch on login state changes.
    // Other values used inside are either:
    // - refetch: stable reference from Apollo
    // - dispensaryId/skipCollectionQuery: guards, not triggers. Changes to these are
    //   already handled by Apollo's query variables/skip behavior, avoiding duplicate refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Use full query data when available, fall back to dispensary data for initial render
  const collectionInfo = collectionData?.getCollection ?? dispensarySuppliedCollection;

  const sortBy = sortByOverride ?? (!hasBrandsFilter ? qp.sortby : undefined);

  // Use the UUID from collectionInfo if available
  // This ensures MongoDB can match against UUIDs in search.productCollections.id
  // If no UUID, skip products query entirely
  const collectionUuid = collectionInfo?.id;

  const { products, loading: productsLoading, error: productsError, totalCount } = useDispensaryProducts({
    filters: { collectionId: collectionUuid, sortBy },
    perPage: limit,
    skip: !collectionUuid || collectionLoading,
  });

  const collection = {
    id: collectionInfo?.id ?? collectionId,
    title: collectionInfo?.title ?? '',
    description: collectionInfo?.description ?? '',
    isExclusive: collectionInfo?.isExclusive ?? false,
    cardBadgeColor: collectionInfo?.cardBadgeColor ?? '',
    image: collectionInfo?.image ?? '',
    customCategory: collectionInfo?.customCategory ?? false,
    enabled: collectionInfo?.enabled ?? false,
    isAccessibleToCurrentUser: collectionInfo?.isAccessibleToCurrentUser ?? false,
    isOutlet: collectionInfo?.isOutlet ?? false,
    outletTimerEnabled: collectionInfo?.outletTimerEnabled ?? false,
    outletTimerMessage: collectionInfo?.outletTimerMessage,
    schedule: collectionInfo?.schedule,
    products: products.map((product) => parseProduct(product)),
    productCount: totalCount,
  };

  const loading = productsLoading || collectionLoading;
  const error = productsError ?? collectionError;

  // Determine how to display the collection based on exclusive status:
  // - 'hidden': Don't render collection at all (exclusive but no access, or user not logged in)
  // - 'exclusive': Render collection with HighlightCard (exclusive with access and user logged in)
  // - 'default': Render collection normally without HighlightCard (non-exclusive)
  let collectionDisplayMode: CollectionDisplayMode = 'default';

  if (collection.isExclusive) {
    if (isLoggedIn && collection.isAccessibleToCurrentUser) {
      collectionDisplayMode = 'exclusive';
    } else {
      collectionDisplayMode = 'hidden';
    }
  }

  return {
    collection,
    loading,
    error,
    collectionDisplayMode,
  };
}
